import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ErrorMessage } from 'secondstep-components'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContextLegacy'
import { SiteType } from 'layers/content/v2/Hocs/withSiteContextLegacy/types'
import { Container } from './component.styles'
import InstanceView from './InstanceView'
import Preview from './Preview'
import EducatorProfile from './EducatorProfile'
import {
  CourseManagerGetter,
  CourseManagerType,
} from 'layers/content/v2/Hocs/CourseManagerGetter'
import { CurriculumCourseManagerGetter } from 'layers/content/v3/Hocs/CurriculumCourseManagerGetter'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { isFlagOn } from 'utils/featureFlags'
import { HS_DASHBOARD_CURRICULUM_FLAG } from './InstanceView/hsDashboardDataSource'

const ViewModes = {
  PREVIEW: 'preview',
  INSTANCE: 'instance',
  EDUCATOR_PROFILE: 'educatorProfile',
}

export const Component = props => {
  const { currentSite, setIsHsInstancedView } = props
  const [isModalLocked, setIsModalLocked] = useState(false)

  if (!currentSite) {
    return <ErrorMessage statusCode={404} />
  }

  function resolveViewMode() {
    if (!currentSite.isSetup) {
      setIsHsInstancedView(false)
      return ViewModes.PREVIEW
    }
    if (
      !currentSite.highSchoolEducatorPreferences?.hasUpdatedProfile ||
      isModalLocked
    ) {
      setIsHsInstancedView(false)
      return ViewModes.EDUCATOR_PROFILE
    }
    setIsHsInstancedView(true)
    return ViewModes.INSTANCE
  }

  function renderView() {
    const viewMode = resolveViewMode()
    switch (viewMode) {
      case ViewModes.PREVIEW:
        return <Preview {...props} />
      case ViewModes.INSTANCE:
        return <InstanceView {...props} />
      case ViewModes.EDUCATOR_PROFILE:
        return (
          <EducatorProfile {...props} setIsModalLocked={setIsModalLocked} />
        )
    }
  }

  return <Container>{renderView()}</Container>
}

Component.propTypes = {
  activeTabProgramsAvailable: PropTypes.array,
  courseManager: CourseManagerType,
  currentSite: SiteType,
  displayTitle: PropTypes.string,
  headerDataTestId: PropTypes.string,
  setIsHsInstancedView: PropTypes.func,
  sortedTeachLinks: PropTypes.array,
  staticLinksAvailable: PropTypes.array,
}

Component.displayName = 'HighSchoolProgramCard'

const courseManagerOptions = {
  // this component is only used for High School:
  programResolver: () => programs.highSchool,
  // Deliberately omits `allowAuthoredCourseTree`. The /dashboard/grades-9-12
  // card must keep using the audienced structure, unlike the High School
  // product path, which resolves leaf node ids from the authored structure.
}

// withSiteContext must wrap the course-manager getters (not the other way
// around) so `currentSite` is injected as a prop the getters can read. In
// particular, CurriculumCourseManagerGetter relies on `currentSite.siteId` to
// resolve the correct active site for singleSite curriculum-service lookups;
// wrapping withSiteContext on the inside would leave siteId undefined and fall
// back to the first site in userContext (the wrong active site). See
// LessonsCurriculum/container.js for the same HOC ordering.
const LegacyCourseManagerWrapped = withSiteContext(
  CourseManagerGetter(Component, courseManagerOptions),
)

const CurriculumCourseManagerWrapped = withSiteContext(
  CurriculumCourseManagerGetter(Component, courseManagerOptions),
)

// Route the HS course-tree fetch through the curriculum service when the HS
// dashboard curriculum flag is on, matching the InstanceView content fetch so
// the whole card flips together. Falls back to the legacy course-manager fetch.
function FlaggedHighSchoolProgramCard(props) {
  if (isFlagOn([HS_DASHBOARD_CURRICULUM_FLAG])) {
    return <CurriculumCourseManagerWrapped {...props} />
  }
  return <LegacyCourseManagerWrapped {...props} />
}

FlaggedHighSchoolProgramCard.displayName = 'HighSchoolProgramCard'

export default FlaggedHighSchoolProgramCard
